// Dependencies.
import SELECT, { ValueType } from 'react-select'
import { SelectComponents } from 'react-select/src/components'
import styled from 'styled-components'
import { CSSProperties, PropsWithChildren, ReactElement } from 'react'

import { colors } from '../../utils'
import { Option } from '../../types'
import { DropdownIndicator } from '../DropdownIndicator'
import { P } from '../P'

// Props.
interface Props<OptionValue = string> {
  instanceId: string // Temporary workaround. Required to use React-Select with SSR. Should be unique to avoid UB.
  className?: string
  components?: Partial<SelectComponents<Option<OptionValue>, false>>
  defaultValue?: Option<OptionValue>
  disabled?: boolean
  placeholder: string
  options: Option<OptionValue>[]
  onChange: (e: ValueType<Option<OptionValue>, false>) => void
  value?: Option<OptionValue>
  singleValueStyles?: CSSProperties // Exposed so we can control this one level up
  errors?: string[]
  isSearchable?: boolean
}

// Select.
export function Select<OptionValue = string>(
  props: PropsWithChildren<Props<OptionValue>>,
): ReactElement | null {
  // Props.
  const {
    className,
    components,
    defaultValue,
    disabled,
    instanceId,
    options,
    placeholder,
    onChange,
    value,
    errors,
    isSearchable,
    singleValueStyles,
  } = props
  // ..
  return (
    <div data-cy="groupTimezone" className="selectComponent">
      <SELECT
        className={`selectInput ${className || ''}`}
        components={{ DropdownIndicator, ...components }}
        defaultValue={defaultValue}
        instanceId={instanceId}
        isDisabled={disabled}
        options={options}
        placeholder={placeholder}
        styles={{
          container: (styles, state) => {
            const border =
              errors && errors.length > 0
                ? `2px solid ${colors.errorRed};`
                : (state as any).isFocused
                ? `2px solid ${colors.washedPurple}`
                : '2px solid #F1F2F5'
            const borderRadius = '6px'
            return { ...styles, border, borderRadius, width: '235px' }
          },
          control: (styles) => ({
            ...styles,
            background: 'none',
            fontWeight: 500,
            height: '45px',
          }),
          indicatorSeparator: (styles) => ({ display: 'none' }),
          menuList: (styles) => ({
            ...styles,
            borderRadius: '6px',
            boxShadow: '0px 10px 16px rgba(0, 0, 0, 0.15)',
          }),
          menu: (styles) => ({
            ...styles,
            zIndex: 10,
          }),
          valueContainer: (styles) => ({ ...styles, height: '30px' }),
          option: (styles, state) => {
            const { isSelected, isFocused } = state
            const { purple, washedPurple, white } = colors
            return {
              ...styles,
              background: (isSelected && purple) || (isFocused && washedPurple),
              fontWeight: 500,
              '&:active': { background: purple },
              '&:hover': { background: !isSelected && washedPurple },
              '&:focus': { background: purple },
            }
          },
          singleValue: (styles) => ({ ...styles, ...singleValueStyles }),
        }}
        onChange={(value: ValueType<Option<OptionValue>, false>) =>
          onChange(value)
        }
        value={value}
        isSearchable={isSearchable ? isSearchable : false}
      />
      {errors && errors.length > 0 && (
        <div className="errorContainer">
          {errors.map((error, index) => {
            return (
              <P
                key={index}
                className="detail"
                style={{ color: `${colors.red}` }}>
                {error}
              </P>
            )
          })}
          <style jsx>{`
            .errorContainer {
              margin-top: 8px;
            }
          `}</style>
        </div>
      )}
    </div>
  )
}
